/** View transform between image coordinates and CSS pixels of the viewport. */

export interface View {
  /** CSS pixels per image pixel. */
  scale: number;
  tx: number;
  ty: number;
}

export const MIN_SCALE = 1 / 64;
export const MAX_SCALE = 256;

export function toScreen(v: View, x: number, y: number): [number, number] {
  return [x * v.scale + v.tx, y * v.scale + v.ty];
}

export function toImage(v: View, sx: number, sy: number): [number, number] {
  return [(sx - v.tx) / v.scale, (sy - v.ty) / v.scale];
}

/** Zoom by `factor` keeping the image point under (sx, sy) fixed. */
export function zoomAt(v: View, sx: number, sy: number, factor: number): View {
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * factor));
  const k = scale / v.scale;
  return { scale, tx: sx - (sx - v.tx) * k, ty: sy - (sy - v.ty) * k };
}

/** Fit an image of size w x h into a viewport of cw x ch CSS pixels with a margin. */
export function fitView(w: number, h: number, cw: number, ch: number, margin = 16): View {
  const scale = Math.max(MIN_SCALE, Math.min((cw - 2 * margin) / w, (ch - 2 * margin) / h));
  return { scale, tx: (cw - w * scale) / 2, ty: (ch - h * scale) / 2 };
}

/** Centre image point (x, y) at a given scale. */
export function centerOn(x: number, y: number, scale: number, cw: number, ch: number): View {
  return { scale, tx: cw / 2 - x * scale, ty: ch / 2 - y * scale };
}
